/**
 * セールスエージェント / 社員 ダッシュボード Phase 1 (v3.2.11 / SA-1)。
 *
 * URL: /sa/dashboard
 *
 * 表示:
 *   - 自分の sales_records (admins/{currentUid}/sales_records) を集計
 *   - サマリ: 未申請 / 申請中 / 支払済 の合計報酬額
 *   - 報酬対象商談数（ユニーク dealId 数）
 *   - レコード一覧（操作なし、read-only）
 *
 * アクセス制御:
 *   - 認証済みユーザーは誰でも閲覧可能
 *   - ただし表示されるのは自分の admins/{uid}/sales_records のみ
 *   - 紹介者 (mail_contacts/{id}/referral_records) はこの画面では扱わない
 *     （紹介者 = 顧客なのでログインユーザーではないため）
 *
 * 2026-10-30: 商談名のリンクは戻り先 (ダッシュボード) を渡す。商談詳細の「前の画面へ」でここへ戻れる。
 */
import {
  COMMISSION_LIST_LABELS,
  COMMISSION_RECORD_STATUS_LABELS,
} from '@goenhub/shared';
import { Award, Coins, FileText, TrendingUp } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { SaRenewalBanner } from './SaRenewalBanner';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import { useMyCommissionRecords } from '@/hooks/useCommissionRecords';
import { useReturnToState } from '@/hooks/useReturnTo';
import { paths } from '@/routes/paths';

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

export function SaDashboardPage() {
  const { user } = useAuth();
  const records = useMyCommissionRecords(user?.uid);
  const returnToState = useReturnToState('ダッシュボード');

  const summary = useMemo(() => {
    const totals = { pending: 0, requested: 0, paid: 0 };
    const dealIds = new Set<string>();
    for (const r of records.data ?? []) {
      totals[r.status] += r.finalAmount;
      dealIds.add(r.dealId);
    }
    return { ...totals, dealCount: dealIds.size };
  }, [records.data]);

  return (
    <Screen
      title="セールス ダッシュボード"
      description="あなたの担当商談・報酬の集計画面。詳細は管理者にお問い合わせください。"
      restoreScroll
    >
      <div className="space-y-4">
        {/* 契約更新バナー (期限接近時のみ表示) */}
        <SaRenewalBanner />

        {/* サマリ */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            icon={<Coins className="size-4 text-[var(--accent-amber,_#d4a373)]" />}
            label="未申請報酬"
            value={`¥${summary.pending.toLocaleString()}`}
          />
          <SummaryCard
            icon={<TrendingUp className="size-4 text-[var(--accent,_#3b82f6)]" />}
            label="申請中報酬"
            value={`¥${summary.requested.toLocaleString()}`}
          />
          <SummaryCard
            icon={<Award className="size-4 text-[var(--accent-green,_#3aaa55)]" />}
            label="支払済報酬"
            value={`¥${summary.paid.toLocaleString()}`}
          />
          <SummaryCard
            icon={<FileText className="size-4 text-[var(--text-muted)]" />}
            label="対象商談数"
            value={`${summary.dealCount} 件`}
          />
        </div>

        {/* レコード一覧 */}
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-2">
            報酬レコード
          </h3>
          {records.isLoading ? (
            <Spinner />
          ) : !records.data || records.data.length === 0 ? (
            <EmptyState
              icon={Coins}
              title="報酬レコードはまだありません"
              description="あなたが担当した商談が入金確定すると、ここに自動で表示されます。"
            />
          ) : (
            <div className="rounded-md border border-[var(--border)] overflow-hidden">
              <div className="grid grid-cols-[80px_2fr_1fr_90px_140px_100px] gap-2 px-3 py-2 bg-[var(--bg-elevated)] text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
                <div>種別</div>
                <div>商談 / 商品</div>
                <div>顧客</div>
                <div>入金日</div>
                <div className="text-right">報酬額</div>
                <div className="text-center">状態</div>
              </div>
              {records.data.map((r) => (
                <div
                  key={r.path}
                  className="grid grid-cols-[80px_2fr_1fr_90px_140px_100px] gap-2 px-3 py-2 items-center text-xs border-t border-[var(--border)] hover:bg-[var(--bg-hover)]"
                >
                  <div>
                    <Badge variant="default">
                      {COMMISSION_LIST_LABELS[r.recipientType]}
                    </Badge>
                  </div>
                  <div className="min-w-0">
                    <Link
                      to={paths.dealDetail.replace(':id', r.dealId)}
                      state={returnToState}
                      className="truncate block font-medium text-[var(--text-primary)] hover:text-[var(--accent)]"
                    >
                      {r.dealTitle}
                    </Link>
                    <div className="truncate text-[10px] text-[var(--text-muted)]">
                      {r.productName}
                    </div>
                  </div>
                  <div className="truncate text-[var(--text-secondary)]">
                    {r.contactName}
                  </div>
                  <div className="text-[var(--text-muted)] tabular-nums">
                    {fmtDate(r.paidAt)}
                  </div>
                  <div
                    className="text-right font-semibold text-[var(--accent-green,_#3aaa55)] tabular-nums"
                    title={r.formula}
                  >
                    ¥{r.finalAmount.toLocaleString()}
                  </div>
                  <div className="text-center">
                    <Badge
                      variant={
                        r.status === 'paid'
                          ? 'approved'
                          : r.status === 'requested'
                            ? 'review'
                            : 'default'
                      }
                    >
                      {COMMISSION_RECORD_STATUS_LABELS[r.status]}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-[10px] text-[var(--text-muted)] italic">
          Phase 1 (read-only)。Phase 2 以降で「商談ボード（担当のみ）」「価格表」「資料集」「報酬計算機」を追加予定。
        </p>
      </div>
    </Screen>
  );
}

function SummaryCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 space-y-1">
      <div className="flex items-center gap-1.5">
        {icon}
        <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
          {label}
        </div>
      </div>
      <div className="text-lg font-semibold tabular-nums text-[var(--text-primary)]">
        {value}
      </div>
    </div>
  );
}
